import { isDefinedArray, isPositiveNumber, reduce } from "et3-type-guards";
import { memoize, sortBy } from "lodash";
import { computed, observable, runInAction } from "mobx";

import { EApprovalPictureStatus } from "../../constants/enum/approval-picture-status";
import { PICTURE_IS_ACTIVE } from "../../constants/input-entries-constants";
import {
  countPendingItems,
  countPendingPictures,
  isPeerDescriptionPending,
  isSelfDescriptionPending
} from "./approval-item-counts";
import { apiCallGetApprovalData } from "../../api/approve-reject-api/api-call-get-approval-data";
import {
  TApprovalAlbumPicture,
  TCorePage,
  TDescriptionType,
  TUserEntry
} from "../../Types";
import getCleanPoolStore from "../core/core-clean-pool";
import getCorePopUpStore from "../core/core-popup-store";
import getCoreUIStore from "../core/core-ui-store";
import getDashBoardStore from "../dashboard-store";
import approveUserDescriptionOnDatabase from "./approve-user-description";
import approveUserPictureOnDatabase from "./approve-user-image";
import rejectUserDescriptionOnDatabase from "./reject-user-description";
import rejectUserPictureOnDatabase from "./reject-user-image";
import { TEt3PickerSingleValue } from "../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../helpers/et3-picker-data-helper";

class ApproveRejectStore {
  selectedLimit = observable.box<TEt3PickerSingleValue>({
    label: "10",
    value: "10"
  });
  approvalData = observable.box<TUserEntry[]>([]);
  actionStatus = observable.map<TDescriptionType, Map<number, boolean>>();
  decidedThisSession = observable.box<number>(0);
  createdFromType = observable.box<TEt3PickerSingleValue>({
    label: "Other",
    value: "other"
  });

  setActionStatus = (type: TDescriptionType, id: number, isPressed: boolean) => {
    const typeMap = reduce(
      () => this.actionStatus.get(type),
      new Map<number, boolean>()
    );
    typeMap.set(id, isPressed);
    runInAction(() => {
      this.actionStatus.set(type, typeMap);
    });
  };

  getActionStatus = (type: TDescriptionType, id: number) => {
    return reduce(() => this.actionStatus.get(type).get(id), false);
  };

  setApprovalData = (data: TUserEntry[]) => {
    runInAction(() => {
      this.approvalData.set(data);
    });
  };

  setSelectedLimit = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.selectedLimit.set(limit);
    });
  };

  setApproveRejectCreatedFromType = (status: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.createdFromType.set(status);
    });
  };

  approveRejectCounter = computed(() => {
    return this.approvalData
      .get()
      .map((userEntry) => countPendingItems(userEntry))
      .reduce((total, pendingItems) => total + pendingItems, 0);
  });

  pendingPicturesCounter = computed(() => {
    return this.approvalData
      .get()
      .map((userEntry) => countPendingPictures(userEntry))
      .reduce((total, pendingPictures) => total + pendingPictures, 0);
  });

  pendingSelfDescriptionCounter = computed(() => {
    return this.approvalData.get().filter(isSelfDescriptionPending).length;
  });

  pendingPeerDescriptionCounter = computed(() => {
    return this.approvalData.get().filter(isPeerDescriptionPending).length;
  });

  usersWithPendingWorkCounter = computed(() => {
    return this.approvalData.get().filter((userEntry) => {
      return isPositiveNumber(countPendingItems(userEntry));
    }).length;
  });

  registerDecision = () => {
    runInAction(() => {
      this.decidedThisSession.set(this.decidedThisSession.get() + 1);
    });
  };

  getAwaitingApprovalData = async () => {
    const definedLimit = reducerToZeroIfUndefined(
      parseInt(this.selectedLimit.get().value, 10)
    );
    const createdFromType = this.createdFromType.get().value;
    if (!isPositiveNumber(definedLimit)) {
      return;
    }
    getCoreUIStore().setIsLoading("APPROVAL_DATA", true);
    const { status, code, data } = await apiCallGetApprovalData({
      createdFromType,
      limit: definedLimit
    });
    if (status === "OK" && isDefinedArray(data)) {
      this.actionStatus.clear();
      this.setApprovalData(data);
    } else if (status === "ERROR") {
      this.setApprovalData([]);
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("APPROVAL_DATA", false);
  };

  cleanPool = async () => {
    await getCleanPoolStore().cleanPool({
      currentData: this.approvalData,
      page: "APPROVAL_DATA",
      poolType: "APPROVAL",
      successVoidFunction: () => this.setApprovalData([])
    });
  };

  approveUserPicture = async (
    userId: number,
    userUUID: string,
    from: TCorePage,
    imageID: string,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);

    await approveUserPictureOnDatabase(
      userId,
      userUUID,
      from,
      imageID,
      resolvingPendindData
    );
    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  rejectUserPicture = async (
    userId: number,
    userUUID: string,
    from: TCorePage,
    imageID: string,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);
    await rejectUserPictureOnDatabase(
      userId,
      userUUID,
      from,
      imageID,
      resolvingPendindData
    );

    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  decreaseDashboardApprovalCount = () => {
    const approvalCount = getDashBoardStore().approvalCount.get();
    if (approvalCount > 0) {
      getDashBoardStore().approvalCount.set(approvalCount - 1);
      sessionStorage.setItem(
        "counts",
        JSON.stringify({
          reportsCount: getDashBoardStore().reportsCount.get(),
          reportedCount: getDashBoardStore().reportedCount.get(),
          approvalCount: getDashBoardStore().approvalCount.get(),
          investigationCasesCount:
            getDashBoardStore().investigationCasesCount.get(),
          activeTime: getDashBoardStore().activeTime.get()
        })
      );
    }
  };
  approveUserDescription = async (
    descriptionID: number,
    userId: number,
    userUUID: string,
    from: TCorePage,
    descriptionType: TDescriptionType,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);
    await approveUserDescriptionOnDatabase(
      descriptionID,
      userId,
      userUUID,
      from,
      descriptionType,
      resolvingPendindData
    );
    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  rejectUserDescription = async (
    descriptionID: number,
    userId: number,
    userUUID: string,
    from: TCorePage,
    descriptionType: TDescriptionType,
    resolvingPendindData: boolean,
    buttonID: string
  ) => {
    getCoreUIStore().setButtonInAction(buttonID, true);
    await rejectUserDescriptionOnDatabase(
      descriptionID,
      userId,
      userUUID,
      from,
      descriptionType,
      resolvingPendindData
    );
    getCoreUIStore().setButtonInAction(buttonID, false);
  };

  orderedAlbumPictureArray = (albumPicture: TApprovalAlbumPicture[]) => {
    return sortBy(albumPicture, [
      (item) => {
        const isPending = item.status === EApprovalPictureStatus.PENDING;
        if (isPending && item.isActive === PICTURE_IS_ACTIVE) {
          return 0;
        }
        if (!isPending) {
          return 1;
        }
        return 2;
      }
    ]);
  };
}

const getApproveRejectStore = memoize(
  () => new ApproveRejectStore(),
  () => 1
);
export default getApproveRejectStore;
